'use client';

import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { useApi } from '../composables/useApi';
import { StandardResponse } from '../models/shared/common';
import { IUserProfile, User } from '../models/user-address';
import { TBiodataInput } from '../modules/user/data/schemas/input';
import { TPasswordChangeRequest } from '../modules/user/data/schemas/request';
import { MAddress } from '../modules/user/models/address';
import { RoutesApi } from '../shared/constants/route';
import { splitFullName } from '../shared/utils/common';

// import { computed, compute } from "zustand-computed-state";
// import { computed } from 'zustand-middleware-computed-state'

type FormLogin = {
  username: string;
  password: string;
};

type StateLogin = {
  form: FormLogin;
  setForm: (fn: (f: FormLogin) => void) => void;
  clear: () => void;
  login: () => void;
};

export interface IAccessRefreshToken {
  token_type: string;
  exp: number;
  jti: string;
  user_id: number;
  username: string;
  gmail: string;
  id: number;
  csrf: string;
  user_agent: string;
  is_staff: boolean;
  groups: any[];
  hgroups: any[];
}

const initialFormLogin: FormLogin = {
  username: '',
  password: '',
};

export const useLogin = create<StateLogin>()(
  immer((set) => ({
    form: { ...initialFormLogin },
    setForm: (fn: any) =>
      set((state) => {
        fn(state.form); // mutate inside immer (like reactive)
      }),

    clear: () =>
      set((s) => {
        s.form = { ...initialFormLogin }; // reset properly
      }),
    login: () => {},
  }))
);

// Authentication State Store
type AuthState = {
  userData: User | null;
  userAddressList: MAddress[];
  selectedUserAddress: MAddress | null;
  userProfileList: IUserProfile[];
  selectedUserProfile: IUserProfile | null;

  secretUser: string;
  secretToken: string;
  accessToken: string;
  refreshToken: string;
  csrfToken: string;
  isLoading: boolean;
  setUserAddress: (userAddress: MAddress) => void;
  setUserProfile: (userProfile: IUserProfile) => void;
  setUserData: (
    values: TBiodataInput,
    user_id: number,
    individual_id: number
  ) => void;
  getUserProfile: () => void;
  getAddressById: (id: number | null) => MAddress | null;
  deleteAddressFromStore: (id: number) => void;
  // getters (read from cookies)
  getAccessToken: () => string;
  getRefreshToken: () => string;
  getCsrfToken: () => string;
  getAccessTokenIdentity: () => IAccessRefreshToken | null;
  getRefreshTokenIdentity: () => IAccessRefreshToken | null;

  verifyOldPassword: (oldPassword: string, user_id: number) => void;
  changePassword: (payload: TPasswordChangeRequest) => void;
  logoutUser: () => void;
};

export const useAuthStore = create<AuthState>()(
  immer((set, get) => ({
    userData: null,
    userAddressList: [],
    selectedUserAddress: null,
    userProfileList: [],
    selectedUserProfile: null,

    secretUser: '',
    secretToken: '',
    accessToken: '',
    refreshToken: '',
    csrfToken: '',
    isLoading: false,
    setUserAddress: (userAddress: MAddress) =>
      set((state) => {
        state.selectedUserAddress = userAddress;
      }),
    setUserProfile: (userProfile: IUserProfile) =>
      set((state) => {
        state.selectedUserProfile = userProfile;
      }),
    setUserData: async (
      values: TBiodataInput,
      user_id: number,
      individual_id: number
    ) => {
      set((state) => {
        state.isLoading = true;
      });
      const { userData } = get();
      const api = useApi();
      const routesApi = RoutesApi();
      const birthdate = values.birthdate.toISOString().split('T')[0];
      const payload = {
        delete: [],
        upsert: [
          {
            model: 'User',
            data: [
              {
                id: user_id ?? userData?.id,
                full_name: values.full_name,
                email: values.email,
                username: userData?.username,
                whatsapp: `62${values.whatsapp}`,
                fk_model: {},
              },
            ],
          },
          {
            model: 'Individual',
            data: [
              {
                id: individual_id ?? userData?.individual_id,
                nickname: values.nickname,
                gender: values.gender,
                birthdate,
                fk_model: null,
              },
            ],
          },
        ],
      };
      try {
        set((state) => {
          state.isLoading = true;
        });

        const response = await api.izmoAuth.post(
          `${routesApi.AUTHENTICATION.UPDATE_BIODATA}?tipe=individual`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${get().getAccessToken()}`,
            },
          }
        );

        //throw error
        const successStatus = [200, 201];
        if (
          response.meta.status &&
          !successStatus.includes(response.meta.status)
        ) {
          throw new Error(response.error?.message || 'Gagal Merubah Biodata');
        }

        const { first_name, last_name } = splitFullName(values.full_name);
        set((s) => {
          if (!s.userData) {
            return;
          }
          s.userData.first_name = first_name;
          s.userData.last_name = last_name;
          s.userData.nickname = values.nickname;
          s.userData.email = values.email;
          s.userData.whatsapp = values.whatsapp;
          s.userData.birthdate = birthdate;
          s.userData.gender = values.gender;
          s.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.isLoading = false;
        });
        throw error;
      }
    },
    getUserProfile: async () => {
      const routesApi = RoutesApi();
      const api = useApi();
      const user = get().getAccessTokenIdentity();
      if (!user) {
        return;
      }

      const url = `${routesApi.SERVICE.GET_USER_PROFILE}?user_id=${user.user_id}`;
      const resp = await api.marketplaceCore.get<
        StandardResponse<IUserProfile[]>
      >(url, {
        headers: {
          Authorization: 'Bearer 123',
        },
      });
      set((state) => {
        state.selectedUserProfile = resp.data.length > 0 ? resp.data[0] : null;
        state.userProfileList = resp.data.length > 0 ? resp.data : [];
      });
    },
    getAddressById: (id: number | null) => {
      if (id == null) {
        return null;
      }
      return get().userAddressList.find((a) => a.id === id) ?? null;
    },
    deleteAddressFromStore: (id: number) => {
      set((state) => {
        state.userAddressList = state.userAddressList.filter(
          (a) => a.id !== id
        );
        if (state.selectedUserAddress?.id === id) {
          state.selectedUserAddress = state.userAddressList[0] ?? null;
        }
      });
    },
    // getters (read from cookies)
    getAccessToken: () => Cookies.get('access_token') ?? '',
    getRefreshToken: () => Cookies.get('refresh_token') ?? '',
    getCsrfToken: () => Cookies.get('csrf_token') ?? '',
    getAccessTokenIdentity: () => {
      const token = get().getAccessToken();
      if (!token) {
        return null;
      }

      try {
        const identity = jwtDecode<IAccessRefreshToken>(token);
        set((state) => {
          state.accessToken = token;
          state.refreshToken = get().getRefreshToken();
        });
        return identity;
      } catch (err) {
        return null;
      }
    },
    getRefreshTokenIdentity: () => {
      const token = get().getRefreshToken();
      if (!token) {
        return null;
      }
      try {
        return jwtDecode<IAccessRefreshToken>(token);
      } catch {
        return null;
      }
    },
    verifyOldPassword: async (oldPassword: string, user_id: number) => {
      const api = useApi();
      const routesApi = RoutesApi();
      const { userData } = get();

      try {
        set((state) => {
          state.isLoading = true;
        });
        const resp = await api.izmoAuth.post(
          `${routesApi.AUTHENTICATION.PASSWORD_RESET}?login=true&id=${user_id ?? userData?.id}`,
          { password: oldPassword },
          {
            headers: {
              Authorization: `Bearer ${get().getAccessToken()}`,
            },
          }
        );
        if (resp.error) {
          throw new Error('Password salah');
        }
        set((state) => {
          ((state.secretToken = resp.data.secretToken),
            (state.secretUser = resp.data.user));
        });
        return true;
      } finally {
        set((state) => {
          state.isLoading = false;
        });
      }
    },
    changePassword: async (payload: TPasswordChangeRequest) => {
      const api = useApi();
      const routesApi = RoutesApi();
      const { secretToken, secretUser } = get();

      try {
        set((state) => {
          state.isLoading = true;
        });
        const resp = await api.izmoAuth.post(
          `${routesApi.AUTHENTICATION.PASSWORD_RESET}/${secretUser}/${secretToken}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${get().getAccessToken()}`,
            },
          }
        );
        if (resp.error) {
          throw new Error('Gagal mengganti password');
        }
        return true;
      } finally {
        set((state) => {
          state.isLoading = false;
        });
      }
    },
    logoutUser: async () => {
      const cookies = Cookies.get();
      Object.keys(cookies).forEach((name) => {
        Cookies.remove(name);
      });
      set((s) => {
        s.userData = null;
        s.userAddressList = [];
        s.selectedUserAddress = null;
        s.userProfileList = [];
        s.selectedUserProfile = null;
        s.secretUser = '';
        s.secretToken = '';
        s.accessToken = '';
        s.refreshToken = '';
        s.csrfToken = '';
        s.isLoading = false;
      });
      const api = useApi();
      const routesApi = RoutesApi();

      try {
        set((state) => {
          state.isLoading = true;
        });
        const resp = await api.healthcareIntelligence.post(
          `${routesApi.AUTHENTICATION.LOGOUT}`,
          {
            headers: {
              Authorization: `Bearer ${get().getAccessToken()}`,
            },
          }
        );
        if (resp.error) {
          throw new Error('Gagal logout');
        }
        return true;
      } finally {
        set((state) => {
          state.isLoading = false;
        });
      }
    },
  }))
);
